/** @jsx jsx */
import React from 'react';
import { jsx } from '@compiled/react';
import Button from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { SectionMessageAction } from '@atlaskit/section-message';
import SearchNoResultsImage from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/empty/components/search-no-results';
import { useIntl } from '@atlassian/jira-intl';
import { ARCHIVES_TABLE_TEST_ID } from '../../../common/ui/constants';
import { useArchiveIssueActions } from '../../../controllers/main.tsx';
import { useArchiveIssueStates } from '../../../controllers/selectors.tsx';
import messages from './messages';

const NoIssuesIllustration = () => (
	<Box xcss={imageWrapperStyles}>
		<SearchNoResultsImage alt="" />
	</Box>
);

export const ArchivesTableEmptyState = ({ noDataState }: { noDataState?: boolean }) => {
	const { formatMessage } = useIntl();
	const { setFilters, setSearchBy, setRefetchArchivesIssuesData } = useArchiveIssueActions();
	const { filters, searchBy } = useArchiveIssueStates();
	const showClearButton = Object.keys(filters).length > 0 || searchBy;
	const clearSearchOnClick = () => {
		setFilters({});
		setSearchBy('');
		setRefetchArchivesIssuesData(true);
	};
	return (
		<Box xcss={emptyIssuesContainerStyles} testId={`${ARCHIVES_TABLE_TEST_ID}.empty-state`}>
			<EmptyState
				imageWidth={228}
				header={
					noDataState
						? formatMessage(messages.emptyStateNoDataMessage)
						: formatMessage(messages.emptyStateMessage)
				}
				description={
					noDataState ? (
						<>
							<Text as="p">{formatMessage(messages.emptyStateNoDataDescriptionMessage)}</Text>
							<SectionMessageAction href="https://support.atlassian.com/jira-software-cloud/docs/archive-an-issue">
								{formatMessage(messages.emptyStateArchiveIssueSupportMessage)}
							</SectionMessageAction>
						</>
					) : (
						<Text as="p">{formatMessage(messages.emptyStateMessagePartTwo)}</Text>
					)
				}
				headingLevel={2}
				primaryAction={
					showClearButton ? (
						<Button
							appearance="primary"
							onClick={() => clearSearchOnClick()}
							testId={`${ARCHIVES_TABLE_TEST_ID}.empty-state.clear-search-button`}
						>
							{formatMessage(messages.clearSearchMessage)}
						</Button>
					) : null
				}
				renderImage={() => <NoIssuesIllustration />}
			/>
		</Box>
	);
};

const imageWrapperStyles = xcss({
	maxHeight: '146px',
	maxWidth: '160px',
	margin: 'auto',
	marginBottom: 'space.300',
});

const emptyIssuesContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	flex: 1,
	position: 'sticky',
	left: 'space.0',
	paddingBlock: 'space.800',
});
